import { z } from "zod";

/**
 * Contract S02 (US-E16.F1-0182 · @UC9231–@UC9234) — DTO create-collection với `amountMode` + union
 * payload 4 mode nâng cao (per_member/tiered/target_split/open_amount) + equal (cũ, không regress).
 *
 * Một nguồn sự thật api (validate biên + shape) ↔ mobile (gọi + map) — DRY.
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: MỌI amount = STRING số nguyên (regex `^\d+$`), server parse → BigInt. KHÔNG float.
 *  - C5 discriminated-union theo `amountMode`, MỖI nhánh `.strict()` — field của mode khác (vd
 *    targetTotalMinor trong per_member) bị reject (chống over-post + nhầm payload).
 *  - currency/fundId/kind/status CỐ Ý VẮNG (kế thừa fund / server set).
 *  - response `remainderBearerMemberId` (target_split — đồng lẻ có tên).
 */

/** 5 chế độ thu — GIÁ TRỊ đồng bộ với @goppy/domain COLLECTION_AMOUNT_MODES + CHECK DB (drift-guard). */
export const COLLECTION_AMOUNT_MODE = {
  EQUAL: "equal",
  PER_MEMBER: "per_member",
  TIERED: "tiered",
  TARGET_SPLIT: "target_split",
  OPEN_AMOUNT: "open_amount",
} as const;

export type CollectionAmountMode =
  (typeof COLLECTION_AMOUNT_MODE)[keyof typeof COLLECTION_AMOUNT_MODE];

/** Số chữ số tối đa cho amount (mirror ADHOC_AMOUNT_MAX_DIGITS — trần PG BIGINT). */
const AMOUNT_MODE_MAX_DIGITS = 19;
/** Trần PG signed BIGINT (chống overflow P2020→500). */
const AMOUNT_MODE_PG_BIGINT_MAX = 9223372036854775807n;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const INTEGER_STRING_PATTERN = /^\d+$/;

const AMOUNT_MODE_TITLE_MAX = 100;
/** Trần số entry/tier/member (chống payload khổng lồ). */
const MAX_ENTRIES = 1000;
const MAX_TIERS = 50;
const TIER_LABEL_MAX = 60;

export const AMOUNT_MODE_INVALID_MESSAGE =
  "Số tiền phải là số nguyên (đơn vị nhỏ nhất)";

/** Số tiền STRING minor dương ≤ trần BIGINT (B1 — reject float/overflow). */
const amountMinorString = z
  .string()
  .trim()
  .min(1, AMOUNT_MODE_INVALID_MESSAGE)
  .max(AMOUNT_MODE_MAX_DIGITS, AMOUNT_MODE_INVALID_MESSAGE)
  .regex(INTEGER_STRING_PATTERN, AMOUNT_MODE_INVALID_MESSAGE)
  .refine((v) => {
    try {
      return BigInt(v) <= AMOUNT_MODE_PG_BIGINT_MAX && BigInt(v) > 0n;
    } catch {
      return false;
    }
  }, AMOUNT_MODE_INVALID_MESSAGE);

function isRealCalendarDate(value: string): boolean {
  const [y, m, d] = value.split("-").map((p) => Number.parseInt(p, 10));
  if (y === undefined || m === undefined || d === undefined) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

const titleField = z.string().trim().min(1).max(AMOUNT_MODE_TITLE_MAX);
const dueDateField = z
  .string()
  .trim()
  .regex(ISO_DATE_PATTERN)
  .refine(isRealCalendarDate);

const memberIdField = z.string().uuid();

/** 1 entry per_member. */
const perMemberEntrySchema = z
  .object({ groupMemberId: memberIdField, amountMinor: amountMinorString })
  .strict();

/** 1 bậc tiered. */
const tierSchema = z
  .object({
    label: z.string().trim().min(1).max(TIER_LABEL_MAX),
    amountMinor: amountMinorString,
  })
  .strict();

/**
 * Superset object `.strict()` cho create-collection theo mode. `amountMode` default 'equal' (body
 * KHÔNG set ⇒ equal, không regress). MỖI field mode-specific OPTIONAL; `.superRefine` chốt bắt buộc
 * đúng mode + reject field mode khác (chống nhầm payload — vd targetTotalMinor trong per_member).
 *
 * Vì là object đơn (KHÔNG union), tương thích `createZodDto` (nestjs-zod). `.strict()` reject field lạ.
 */
export const createCollectionWithModeRequestSchema = z
  .object({
    title: titleField,
    amountMode: z
      .enum([
        COLLECTION_AMOUNT_MODE.EQUAL,
        COLLECTION_AMOUNT_MODE.PER_MEMBER,
        COLLECTION_AMOUNT_MODE.TIERED,
        COLLECTION_AMOUNT_MODE.TARGET_SPLIT,
        COLLECTION_AMOUNT_MODE.OPEN_AMOUNT,
      ])
      .default(COLLECTION_AMOUNT_MODE.EQUAL),
    dueDate: dueDateField,
    // equal:
    amountMinor: amountMinorString.optional(),
    // per_member:
    entries: z.array(perMemberEntrySchema).min(1).max(MAX_ENTRIES).optional(),
    // tiered:
    tiers: z.array(tierSchema).min(1).max(MAX_TIERS).optional(),
    memberTierMap: z
      .record(memberIdField, z.string().trim().min(1).max(TIER_LABEL_MAX))
      .optional(),
    // target_split:
    targetTotalMinor: amountMinorString.optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    /** Field CHỈ hợp lệ ở đúng mode; hiện diện ở mode khác ⇒ reject (chống nhầm/over-post). */
    const requireField = (present: boolean, path: string, mode: string) => {
      if (!present) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${path} bắt buộc với amountMode='${mode}'`,
        });
      }
    };
    const forbidField = (present: boolean, path: string) => {
      if (present) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${path} không hợp lệ với amountMode='${val.amountMode}'`,
        });
      }
    };

    const hasAmount = val.amountMinor !== undefined;
    const hasEntries = val.entries !== undefined;
    const hasTiers = val.tiers !== undefined || val.memberTierMap !== undefined;
    const hasTarget = val.targetTotalMinor !== undefined;

    switch (val.amountMode) {
      case COLLECTION_AMOUNT_MODE.EQUAL:
        requireField(hasAmount, "amountMinor", "equal");
        forbidField(hasEntries, "entries");
        forbidField(val.tiers !== undefined, "tiers");
        forbidField(val.memberTierMap !== undefined, "memberTierMap");
        forbidField(hasTarget, "targetTotalMinor");
        break;
      case COLLECTION_AMOUNT_MODE.PER_MEMBER:
        requireField(hasEntries, "entries", "per_member");
        forbidField(hasAmount, "amountMinor");
        forbidField(val.tiers !== undefined, "tiers");
        forbidField(val.memberTierMap !== undefined, "memberTierMap");
        forbidField(hasTarget, "targetTotalMinor");
        break;
      case COLLECTION_AMOUNT_MODE.TIERED:
        requireField(val.tiers !== undefined, "tiers", "tiered");
        requireField(val.memberTierMap !== undefined, "memberTierMap", "tiered");
        forbidField(hasAmount, "amountMinor");
        forbidField(hasEntries, "entries");
        forbidField(hasTarget, "targetTotalMinor");
        break;
      case COLLECTION_AMOUNT_MODE.TARGET_SPLIT:
        requireField(hasTarget, "targetTotalMinor", "target_split");
        forbidField(hasAmount, "amountMinor");
        forbidField(hasEntries, "entries");
        forbidField(hasTiers, "tiers");
        break;
      case COLLECTION_AMOUNT_MODE.OPEN_AMOUNT:
        forbidField(hasAmount, "amountMinor");
        forbidField(hasEntries, "entries");
        forbidField(hasTiers, "tiers");
        forbidField(hasTarget, "targetTotalMinor");
        break;
    }
  });

export type CreateCollectionWithModeRequest = z.infer<
  typeof createCollectionWithModeRequestSchema
>;

/**
 * Shape response 201 cho create-collection theo mode. amount trả STRING (B1). remainderBearerMemberId
 * chỉ có ở target_split (đồng lẻ có tên) — null các mode khác.
 */
export const createCollectionWithModeResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    fundId: z.string().uuid(),
    title: z.string(),
    amountMode: z.enum([
      COLLECTION_AMOUNT_MODE.EQUAL,
      COLLECTION_AMOUNT_MODE.PER_MEMBER,
      COLLECTION_AMOUNT_MODE.TIERED,
      COLLECTION_AMOUNT_MODE.TARGET_SPLIT,
      COLLECTION_AMOUNT_MODE.OPEN_AMOUNT,
    ]),
    currency: z.string().length(3),
    dueDate: z.string().regex(ISO_DATE_PATTERN),
    duesCreated: z.number().int().nonnegative(),
    /** target_split: member gánh đồng lẻ; các mode khác = null. */
    remainderBearerMemberId: z.string().uuid().nullable(),
  })
  .strict();

export type CreateCollectionWithModeResponse = z.infer<
  typeof createCollectionWithModeResponseSchema
>;
